import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlagIcon, CheckCircleIcon, MessageCircleIcon, AlertCircleIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
import { useClient } from './ClientContext';
const weeks = [{
  label: 'Week 1',
  phases: [{
    name: 'Deep dive session',
    color: '#6366F1',
    span: 60
  }, {
    name: 'Research & analysis',
    color: '#818CF8',
    span: 40
  }],
  pitstop: 'Check-in: Share initial findings & confirm direction'
}, {
  label: 'Week 2',
  phases: [{
    name: 'Logo design exploration',
    color: '#F59E0B',
    span: 100
  }],
  pitstop: 'Check-in: Present logo concepts for feedback'
}, {
  label: 'Week 3',
  phases: [{
    name: 'Logo refinement & colour palette',
    color: '#3B82F6',
    span: 60
  }, {
    name: 'Typography direction',
    color: '#2563EB',
    span: 40
  }],
  pitstop: 'Check-in: Review refined logo & brand elements'
}, {
  label: 'Week 4',
  phases: [{
    name: 'Brand guidelines & assets',
    color: '#10B981',
    span: 50
  }, {
    name: 'Final delivery & handover',
    color: '#059669',
    span: 50
  }],
  pitstop: 'Check-in: Final review & sign-off'
}];
export function WeeklyTimeline() {
  const {
    companyName
  } = useClient();
  const [hoveredWeek, setHoveredWeek] = useState<number | null>(null);
  const [expandedDisclaimer, setExpandedDisclaimer] = useState(false);
  return <section id="timeline" className="py-24 px-6 overflow-hidden bg-offwhite" data-id="element-244">
      <div className="max-w-7xl mx-auto" data-id="element-245">
        <ScrollReveal data-id="element-246">
          <div className="mb-12" data-id="element-247">
            <span className="text-accent font-bold tracking-wider text-sm" data-id="element-248">
              Schedule
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-navy mt-2" data-id="element-249">
              Project timeline
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl" data-id="element-250">
              A focused 4-week sprint with weekly check-ins to keep everything
              on track.
            </p>
          </div>
        </ScrollReveal>

        {/* Modern Timeline */}
        <div className="mt-8 space-y-0" data-id="element-251">
          {weeks.map((week, weekIndex) => <ScrollReveal key={week.label} delay={weekIndex * 0.1} data-id="element-252">
              <motion.div className="relative" onHoverStart={() => setHoveredWeek(weekIndex)} onHoverEnd={() => setHoveredWeek(null)} data-id="element-253">
                {/* Week row */}
                <div className="flex items-stretch min-h-[100px] group" data-id="element-254">
                  {/* Week label column */}
                  <div className="w-24 md:w-32 flex-shrink-0 flex flex-col items-center justify-center relative" data-id="element-255">
                    <motion.div className="text-sm font-extrabold text-navy tracking-wider" animate={{
                  scale: hoveredWeek === weekIndex ? 1.05 : 1
                }} transition={{
                  duration: 0.2
                }} data-id="element-256">
                      {week.label}
                    </motion.div>
                    {/* Vertical connector line */}
                    {weekIndex < weeks.length - 1 && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0.5 h-6 bg-gray-200 translate-y-3" data-id="element-257" />}
                  </div>

                  {/* Phase bars */}
                  <div className="flex-1 flex flex-col justify-center py-3" data-id="element-258">
                    <div className="flex gap-0 rounded-xl overflow-hidden shadow-sm" data-id="element-259">
                      {week.phases.map((phase, phaseIndex) => <motion.div key={phase.name} className="relative h-14 flex items-center justify-center px-4 cursor-default" style={{
                    backgroundColor: phase.color,
                    width: `${phase.span}%`
                  }} initial={{
                    scaleX: 0,
                    opacity: 0
                  }} whileInView={{
                    scaleX: 1,
                    opacity: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.5,
                    delay: weekIndex * 0.15 + phaseIndex * 0.1,
                    ease: 'easeOut'
                  }} data-id="element-260">
                          <span className="text-white text-xs md:text-sm font-semibold text-center truncate" data-id="element-261">
                            {phase.name}
                          </span>
                        </motion.div>)}
                    </div>

                    {/* Pitstop / Check-in */}
                    <motion.div className="flex items-center gap-2 mt-3 ml-2" initial={{
                  opacity: 0,
                  x: -10
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: weekIndex * 0.15 + 0.3
                }} data-id="element-262">
                      <div className="flex items-center gap-1.5" data-id="element-263">
                        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" data-id="element-264" />
                        <FlagIcon className="w-3.5 h-3.5 text-accent" data-id="element-265" />
                      </div>
                      <span className="text-xs text-gray-500 font-medium" data-id="element-266">
                        {week.pitstop}
                      </span>
                    </motion.div>
                  </div>
                </div>

                {/* Divider between weeks (no gap) */}
                {weekIndex < weeks.length - 1 && <div className="ml-24 md:ml-32 border-t border-dashed border-gray-200" data-id="element-267" />}
              </motion.div>
            </ScrollReveal>)}
        </div>

        {/* Modern Disclaimer - Framer/Webflow style */}
        <ScrollReveal delay={0.3} data-id="element-268">
          <motion.div className="mt-12 relative overflow-hidden rounded-2xl bg-navy" layout data-id="element-269">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full -mr-32 -mt-32 blur-3xl" data-id="element-270" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full -ml-24 -mb-24 blur-3xl" data-id="element-271" />

            <div className="relative z-10 p-8 md:p-10" data-id="element-272">
              <div className="flex items-start gap-4" data-id="element-273">
                <motion.div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center" whileHover={{
                rotate: 15
              }} transition={{
                type: 'spring',
                stiffness: 300
              }} data-id="element-274">
                  <AlertCircleIcon className="w-5 h-5 text-amber-400" data-id="element-275" />
                </motion.div>
                <div className="flex-1" data-id="element-276">
                  <h4 className="text-white font-bold text-lg mb-3" data-id="element-277">
                    Disclaimer
                  </h4>
                  <motion.div className="overflow-hidden" animate={{
                  height: expandedDisclaimer ? 'auto' : '48px'
                }} transition={{
                  duration: 0.4,
                  ease: 'easeInOut'
                }} data-id="element-278">
                    <p className="text-gray-300 leading-relaxed text-sm" data-id="element-279">
                      Subject to change depending on start date, scope of work
                      and feedback from the client. Please note that any delays
                      in approval or receipt of feedback will extend the project
                      schedule. Please note that certain stages can be fulfilled
                      in tandem to ensure a speedy turnaround time.
                    </p>
                  </motion.div>
                  <button onClick={() => setExpandedDisclaimer(!expandedDisclaimer)} className="mt-3 text-accent text-xs font-bold tracking-wider hover:text-accent-hover transition-colors flex items-center gap-1" data-id="element-280">
                    {expandedDisclaimer ? 'SHOW LESS' : 'READ MORE'}
                    <motion.span animate={{
                    rotate: expandedDisclaimer ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-281">
                      ↓
                    </motion.span>
                  </button>
                </div>
              </div>

              {/* Interactive progress indicator */}
              <div className="mt-8 flex items-center gap-3" data-id="element-282">
                {weeks.map((week, i) => <motion.div key={i} className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden" whileHover={{
                scaleY: 3
              }} transition={{
                duration: 0.2
              }} data-id="element-283">
                    <motion.div className="h-full rounded-full" style={{
                  backgroundColor: weeks[i].phases[0].color
                }} initial={{
                  width: '0%'
                }} whileInView={{
                  width: '100%'
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.8,
                  delay: i * 0.2
                }} data-id="element-284" />
                  </motion.div>)}
              </div>
              <div className="flex justify-between mt-2" data-id="element-285">
                {weeks.map((week, i) => <span key={i} className="text-[10px] text-gray-500 font-medium" data-id="element-286">
                    {week.label}
                  </span>)}
              </div>
            </div>
          </motion.div>
        </ScrollReveal>
      </div>
    </section>;
}